import React, { useState, useEffect, useRef } from "react";
import { centsToDollars } from "../../../app/utils/models/checkers/checkCurrencyToDollars";
import { List, Space, Tag, Button, Modal, Select, Input } from "antd";
import { RollbackOutlined, MailOutlined } from "@ant-design/icons";
import NumberFormat from "react-number-format";
import RefundModal from "../../invoices/components/RefundModal";
import TextSubtitle from "../../../app/system-components/typography/text/TextSubtitle";
import { capitalizeFirstLetter } from "../../../app/utils/casing/capitalizeFirstLetter";

import { DB, functionsBaseUrl } from "../../../app/config/firebase";
import {
  COLOR_LIGHT_GRAY,
  COLOR_LIGHT_GREEN,
  COLOR_TEXT_GRAY_1,
  COLOR_YELLOW_0,
} from "../../../app/ui/colorConstants";
import { Typography } from "antd";
import { useSelector } from "react-redux";
import { authSelector } from "../../auth/authSlice";
import { useSnackbar } from "notistack";

const PaymentList = ({ fetching, data, invoiceId }) => {

  console.log("data: ", data);
  
  const { orgData } = useSelector(authSelector);

  const [loadingRefund, _setLoadingRefund] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [paymentToRefund, setPaymentToRefund] = useState(null);
  const [payments, setPayments] = useState(data);
  const [loadingSendReceipt, setLoadingSendReceipt] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState('');
  const [customEmail, setCustomEmail] = useState('');
  
  const { enqueueSnackbar } = useSnackbar();

  const fetchJustifiPaymentDetails = async (paymentsToCheck) => {

    if (!orgData) return;

    try {
      const justifiPayments = paymentsToCheck.filter((item) => item.paymentMethod === "justifi");
      
      if (!justifiPayments.length || !orgData?.id) {
        console.log("No Justifi payments to fetch");
        return;
      }

      const paymentIds = justifiPayments
        .map(payment => payment.processorSpecific?.justifiPaymentId)
        .filter(Boolean);

      if (!paymentIds.length) {
        console.log("No valid Justifi payment IDs found");
        return;
      }

      console.log("Fetching details for payment IDs:", paymentIds);
      
      const response = await fetch(`${functionsBaseUrl}/justifi`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          orgId: orgData.id,
          action: "getPaymentsById",
          payload: {
            ids: paymentIds,
          }
        })
      });

      if (!response.ok) {
        throw new Error("Failed to fetch payment details");
      }

      const result = await response.json();
      console.log("Justifi payment details:", result);

      // Update payments with Justifi status and check for status changes
      const updatedPayments = await Promise.all(paymentsToCheck.map(async payment => {
        if (payment.paymentMethod !== "justifi") return payment;
        
        const justifiPayment = result.find(jp => 
          jp.data.id === payment.processorSpecific?.justifiPaymentId
        );

        if (!justifiPayment) return payment;

        const newStatus = justifiPayment.data.status === "succeeded" ? "complete" : justifiPayment.data.status;

        // Update Firestore if status has changed to complete or failed
        if ((payment.status !== "complete" && newStatus === "complete") || 
            (payment.status !== "failed" && newStatus === "failed")) {
          console.log(`Payment status changed to ${newStatus}, updating Firestore:`, payment);
          await DB.collection("orgs")
            .doc(orgData.id)
            .collection("payments")
            .doc(payment.id)
            .update({ 
              status: newStatus,
              errorMsg: newStatus === "failed" ? justifiPayment.data.error_description || "Payment failed" : null
            });
        }

        return {
          ...payment,
          status: newStatus,
          errorMsg: newStatus === "failed" ? justifiPayment.data.error_description || "Payment failed" : null
        };
      }));

      setPayments(updatedPayments);
    } catch (error) {
      console.error("Error fetching Justifi payment details:", error);
    }
  };

  // Update payments and fetch details when data changes
  useEffect(() => {
    setPayments(data);
    if (data.length && orgData) {
      fetchJustifiPaymentDetails(data);
    }
  }, [data, orgData]);

  const handleSendReceipt = async (payment, email) => {
    try {
      setLoadingSendReceipt(true);

      const url = functionsBaseUrl + "emailService";
      console.log('Sending receipt request to:', url);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          orgId: orgData.id,
          action: 'sendReceiptEmail',
          payload: {
            paymentId: payment.id,
            overrideEmail: email
          }
        })
      });

      // First check if we got a response
      if (!response) {
        throw new Error('No response from server');
      }

      // Then check if the response is ok
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        console.error('Email service error:', {
          status: response.status,
          statusText: response.statusText,
          errorData
        });
        throw new Error(errorData?.message || `HTTP error! status: ${response.status}`);
      }

      // Try to parse the response
      const result = await response.json();
      console.log('Email service response:', result);

      if (result.status === "success") {
        enqueueSnackbar("Receipt sent", { variant: "success" });
      } else {
        throw new Error(result.msg || 'Failed to send receipt');
      }
    } catch (error) {
      console.error('Error sending receipt:', error);
      const errorMessage = error.message === 'Failed to fetch' 
        ? "Unable to connect to the server. Please check your internet connection and try again."
        : error.message || "Failed to send receipt. Please try again.";
      
      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      setLoadingSendReceipt(false);
      setShowReceiptModal(false);
      setSelectedEmail('');
      setCustomEmail('');
    }
  };

  const openReceiptModal = (payment) => {
    setSelectedPayment(payment);
    setSelectedEmail(payment.customer?.email || '');
    setShowReceiptModal(true);
  };

  const getEmailOptions = (payment) => {
    if (!payment?.customer) return [];
    
    const options = [];
    
    // Add primary email
    if (payment.customer.email) {
      options.push({
        value: payment.customer.email,
        label: `${payment.customer.email} (Primary)`
      });
    }

    // Add additional contacts
    if (payment.customer.additionalContacts) {
      payment.customer.additionalContacts.forEach(contact => {
        if (contact.email) {
          options.push({
            value: contact.email,
            label: `${contact.email} (${contact.name || 'Additional Contact'})`
          });
        }
      });
    }

    return options;
  };

  function refundClicked(item) {
    setPaymentToRefund(item);
    setShowRefundModal(true);
  }

  function onRefundComplete(success) {
    setShowRefundModal(false);
    if (success) {
      enqueueSnackbar("Refund complete", { variant: "success" });
    } else {
      enqueueSnackbar("Unable to process refund.", { variant: "error" });
    }
  }

  return (
    <div>
      <List
        loading={fetching}
        locale={{ emptyText: "No payments" }}
        size="small"
        bordered={false}
        dataSource={payments}
        renderItem={(item) => (
          <List.Item>
            <List.Item.Meta
              title={
                <Space>
                  <NumberFormat
                    style={{ fontWeight: "bold" }}
                    value={item?.total.toFixed(2)}
                    displayType={"text"}
                    thousandSeparator={true}
                    prefix={"$"}
                  />

                  <Tag
                    style={{
                      borderColor: "transparent",
                      backgroundColor: COLOR_LIGHT_GRAY,
                    }}
                  >
                    {capitalizeFirstLetter(item?.paymentMethod ?? "")}
                  </Tag>
                </Space>
              }
              description={
                <Space direction={"vertical"} size={0}>
                  {item?.paymentMethod === "stripe" && (
                    <div>
                      <Space>
                        Processing Fees:
                        <NumberFormat
                          style={{ fontWeight: "bold" }}
                          value={item?.stripeFees?.toFixed(2)}
                          displayType={"text"}
                          thousandSeparator={true}
                          prefix={"$"}
                        />
                      </Space>
                      <br />
                      <Space>
                        Net:
                        <NumberFormat
                          style={{ fontWeight: "bold" }}
                          value={item?.stripeNet?.toFixed(2)}
                          displayType={"text"}
                          thousandSeparator={true}
                          prefix={"$"}
                        />
                      </Space>
                    </div>
                  )}
                  <TextSubtitle text={`Ref No: ${item.refNo}`} />

                  {item?.paymentMethodDetails && (
                    <TextSubtitle 
                      text={`Paid with ${capitalizeFirstLetter(item?.paymentMethodDetails?.brand)} •••• ${item?.paymentMethodDetails?.last4}`}
                    />
                  )}

                  {
                    item?.paymentMethod === "justifi" && (
                      <div>
                        <TextSubtitle text={`Payment ID: ${item?.processorSpecific?.justifiPaymentId}`} />
                        <br />
                        <TextSubtitle text={`Checkout ID: ${item?.processorSpecific?.justifiCheckoutId}`} />
                      </div>
                    )
                  }
                  <TextSubtitle
                    style={{ color: COLOR_TEXT_GRAY_1, fontSize: "12px" }}
                    text={item?.paymentDate?.toDate().toLocaleString()}
                  />
                  {item.refunds.length > 0 && (
                    <div style={{ float: "right", marginLeft: "10px" }}>
                      <br />
                      <Typography.Title level={5} style={{ margin: 0 }}>
                        Refunds
                      </Typography.Title>

                      {item.refunds.map((item, _index) => {
                        return (
                          <div
                            style={{
                              borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
                              marginBottom: "8px",
                            }}
                          >
                            <TextSubtitle
                              style={{
                                color: COLOR_TEXT_GRAY_1,
                                fontSize: "12px",
                              }}
                              text={"Amount"}
                            />
                            <NumberFormat
                              style={{ fontWeight: "bold", marginLeft: "5px" }}
                              value={centsToDollars(item.amount).toFixed(2)}
                              displayType={"text"}
                              thousandSeparator={true}
                              prefix={"$"}
                            />
                            <br />
                            <TextSubtitle
                              style={{
                                color: COLOR_TEXT_GRAY_1,
                                fontSize: "12px",
                              }}
                              text={`Date: ${item.date
                                .toDate()
                                .toLocaleString()}`}
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Space>
              }
            />

            <div style={{ position: "relative" }}>
              <Tag
                style={{
                  display: "block",
                  // marginBottom: "10px",
                  position: "absolute",
                  bottom: "20px",
                  right: "10px",
                  borderColor:
                    item.status === "complete" ? "green" : "transparent",
                  backgroundColor:
                    item.status === "complete"
                      ? COLOR_LIGHT_GREEN
                      : item.status === "pending"
                      ? COLOR_YELLOW_0
                      : COLOR_LIGHT_GRAY,
                }}
              >
                {item.status && capitalizeFirstLetter(item.status)}
              </Tag>
              {console.log("item status: ", item.status)}
              {item.status === "complete" ? (
                <div style={{
                  display: "flex",
                  gap: "8px",
                  position: "absolute",
                  top: "5px",
                  right: "10px"
                }}>
                  <Button
                    icon={<MailOutlined />}
                    onClick={() => openReceiptModal(item)}
                    title={`Send receipt to ${item.customer?.email || 'customer'}`}
                    loading={loadingSendReceipt}
                  >
                    Send Receipt
                  </Button>
                  <Button
                    type="primary"
                    icon={<RollbackOutlined />}
                    loading={loadingRefund}
                    onClick={() => refundClicked(item)}
                  >
                    Refund Payment
                  </Button>
                </div>
              ) : (
                ""
              )}
            </div>
          </List.Item>
        )}
      />

      <Modal
        title="Send Receipt"
        open={showReceiptModal}
        onOk={() => handleSendReceipt(selectedPayment, selectedEmail === 'custom' ? customEmail : selectedEmail)}
        onCancel={() => {
          setShowReceiptModal(false);
          setSelectedEmail('');
          setCustomEmail('');
        }}
        confirmLoading={loadingSendReceipt}
      >
        <div style={{ marginBottom: 16 }}>
          <p>Select email address to send receipt to:</p>
          <Select
            style={{ width: '100%', marginBottom: 16 }}
            value={selectedEmail}
            onChange={setSelectedEmail}
            options={[
              ...getEmailOptions(selectedPayment),
              { value: 'custom', label: 'Custom Email' }
            ]}
          />
          {selectedEmail === 'custom' && (
            <Input
              placeholder="Enter email address"
              value={customEmail}
              onChange={(e) => setCustomEmail(e.target.value)}
            />
          )}
        </div>
      </Modal>

      <RefundModal
        visible={showRefundModal}
        onCancel={() => setShowRefundModal(false)}
        onComplete={onRefundComplete}
        paymentDoc={paymentToRefund}
        invoiceId={invoiceId}
      />
    </div>
  );
};

export default PaymentList;
